import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle,
  Dropdown, Field, MessageBar, MessageBarBody, Option, Text, Textarea, tokens,
} from '@fluentui/react-components';
import { useQueryClient } from '@tanstack/react-query';
import {
  CANCEL_REASONS, reviewsApi,
  type BulkReviewAction, type BulkReviewRequest, type BulkReviewResult, type ClientReview,
} from '../../api/reviews';
import { REVIEWER_ASSIGNMENTS_QUERY_KEY, staffProfilesApi, type StaffProfile } from '../../api/staffProfiles';
import { MY_MOMENTS_QUERY_KEY } from '../../api/moments';
import type { SystemUser } from '../../types';
import { apiErrorText } from './ReviewCoverageControls';
import i18n, { useTranslation } from '../../i18n';
import { cancelReasonLabel } from '../../api/reviews';

/**
 * Bulk actions on the Reviews grid — docs/specs/reviews-bulk-actions.md.
 *
 * 🔑 The server decides every row with the SAME rule code as the single-row paths; this file only
 * selects, confirms and reports. The confirmation is built from a DRY RUN of the same request, so the
 * numbers in the dialog are the server's, never a client-side guess (spec §3).
 */

// ── Selection (spec §1) ──────────────────────────────────────────────────────────────────────

export interface ReviewSelection {
  selected: ReadonlySet<string>;
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Header checkbox: select or clear every VISIBLE (filtered) row. */
  setAllVisible: (checked: boolean) => void;
  clear: () => void;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  /** "3 selected reviews are hidden by this filter and were deselected." — or null. */
  notice: string | null;
  dismissNotice: () => void;
}

/**
 * Selection is a set of review ids. When the visible set changes (a filter is applied), selected rows
 * that the filter hides are DESELECTED and the page says how many, so a bulk action can never reach a
 * row the user cannot see. Not restored across navigation (spec §6) — a stale selection after Back is
 * how a bulk action reaches the wrong rows.
 */
export function useReviewSelection(visibleIds: readonly string[]): ReviewSelection {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const visibleKey = visibleIds.join('|');
  const visible = useMemo(() => new Set(visibleIds), [visibleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setSelected((prev) => {
      const kept = new Set([...prev].filter((id) => visible.has(id)));
      const dropped = prev.size - kept.size;
      if (dropped > 0) {
        setNotice(i18n.t('bulk.hidden', { count: dropped }));
        return kept;
      }
      return prev;
    });
  }, [visible]);

  const visibleSelectedCount = [...visible].filter((id) => selected.has(id)).length;
  return {
    selected,
    isSelected: (id) => selected.has(id),
    toggle: (id) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    setAllVisible: (checked) => setSelected(checked ? new Set(visible) : new Set()),
    clear: () => setSelected(new Set()),
    allVisibleSelected: visible.size > 0 && visibleSelectedCount === visible.size,
    someVisibleSelected: visibleSelectedCount > 0 && visibleSelectedCount < visible.size,
    notice,
    dismissNotice: () => setNotice(null),
  };
}

// ── Confirmation text (spec §3: counts, not adjectives) ──────────────────────────────────────

/**
 * `label` is the toolbar button; `verb` starts the sentence and the confirm button ("Complete 9
 * reviews"); `past` is the report ("9 completed"). Read in the UI language at the moment of use.
 */
const VERB = {
  label: (a: BulkReviewAction) => i18n.t(`bulk.label.${a}`),
  verb: (a: BulkReviewAction) => i18n.t(`bulk.verb.${a}`),
  past: (a: BulkReviewAction, count: number) => i18n.t(`bulk.past.${a}`, { count }),
};

const reviewsCount = (count: number) => i18n.t('bulk.count.review', { count });
const clientsCount = (count: number) => i18n.t('bulk.count.client', { count });

/** "Cancel 12 reviews for 11 clients." plus what is notable about them, from the dry run. */
export function describeDryRun(
  action: BulkReviewAction,
  preview: BulkReviewResult,
  rows: readonly ClientReview[],
  reviewerName?: string,
): string[] {
  const doneIds = new Set(preview.results.filter((r) => r.outcome === 'done').map((r) => r.id));
  const doneRows = rows.filter((r) => doneIds.has(r.id));
  const clients = new Set(doneRows.map((r) => r.bookkeepingId)).size;
  const lines = [
    i18n.t('bulk.dryRun.head', {
      verb: VERB.verb(action),
      reviews: reviewsCount(doneIds.size),
      clients: clientsCount(clients),
      to: reviewerName ? i18n.t('bulk.dryRun.to', { name: reviewerName }) : '',
    }),
  ];
  const inProgress = doneRows.filter((r) => r.status === 'in-progress').length;
  const withDocs = doneRows.filter((r) => (r.financialDocuments?.length ?? 0) > 0).length;
  const notable = [
    inProgress > 0 && action !== 'complete' ? i18n.t('bulk.dryRun.inProgress', { count: inProgress }) : null,
    withDocs > 0 && action === 'cancel' ? i18n.t('bulk.dryRun.withDocs', { count: withDocs }) : null,
    action === 'complete'
      ? (() => {
          const open = doneRows.filter((r) => r.checklistItems.some((i) => !i.isChecked)).length;
          return open > 0 ? i18n.t('bulk.dryRun.unticked', { count: open }) : null;
        })()
      : null,
  ].filter(Boolean);
  if (notable.length) lines.push(notable.join(' · ') + '.');
  const skipped = preview.results.filter((r) => r.outcome === 'skipped');
  if (skipped.length) lines.push(i18n.t('bulk.dryRun.skipped', { count: skipped.length, reasons: groupReasons(skipped) }));
  return lines;
}

/** "no email sent (Leandre Logistics LLC, Ft Myers Tile Service LLC); not pending (JNS Courier LLC)". */
function groupReasons(rows: BulkReviewResult['results']): string {
  const by = new Map<string, string[]>();
  for (const r of rows) {
    const key = r.reason ?? i18n.t('bulk.noReason');
    by.set(key, [...(by.get(key) ?? []), r.clientName ?? r.id]);
  }
  return [...by.entries()].map(([reason, names]) => `${reason} (${names.join(', ')})`).join('; ');
}

/**
 * Whether the report warns: any failed row, or a future-reviewer step that did not happen. Decided
 * from the OUTCOME — the old rule matched /failed|not changed/ in the English sentence, which a
 * Spanish report would never contain, so a failure would have shown as a success.
 */
export function reportWarns(result: BulkReviewResult, futureOk: boolean): boolean {
  return result.summary.failed > 0 || !futureOk;
}

/** The report after execution — "9 completed, 3 skipped: …". Never silent partial success. */
export function describeOutcome(action: BulkReviewAction, result: BulkReviewResult): string {
  const { done, skipped, failed } = result.summary;
  const parts = [VERB.past(action, done)];
  if (skipped) parts.push(i18n.t('bulk.outcome.skipped', { count: skipped, reasons: groupReasons(result.results.filter((r) => r.outcome === 'skipped')) }));
  if (failed) parts.push(i18n.t('bulk.outcome.failed', { count: failed, reasons: groupReasons(result.results.filter((r) => r.outcome === 'failed')) }));
  return parts.join(', ') + '.';
}

// ── Batching (spec §4: 200 ids per call; the UI batches above that and merges the reports) ──

export const BULK_BATCH_SIZE = 200;

/** Runs one bulk request in batches of {@link BULK_BATCH_SIZE} and merges the per-row reports. */
export async function runBulk(body: BulkReviewRequest): Promise<BulkReviewResult> {
  const results: BulkReviewResult['results'] = [];
  let bulkId: string | null = null;
  for (let i = 0; i < body.ids.length; i += BULK_BATCH_SIZE) {
    const part = await reviewsApi.bulk({
      ...body,
      ids: body.ids.slice(i, i + BULK_BATCH_SIZE),
      ...(bulkId ? { bulkId } : {}),
    });
    results.push(...part.results);
    bulkId ??= part.bulkId;
  }
  const count = (o: string) => results.filter((r) => r.outcome === o).length;
  return {
    bulkId,
    dryRun: !!body.dryRun,
    action: body.action,
    results,
    summary: { done: count('done'), skipped: count('skipped'), failed: count('failed') },
  };
}

// ── "Also their future reviews" (spec §2, reassign) ─────────────────────────────────────────

/**
 * Points the clients whose reviews ACTUALLY moved at the new reviewer, through the EXISTING
 * soft-delete-then-insert writer, so `core.ReviewerAssignment` keeps its attribution history (B6).
 * Returns a sentence for the report. It never throws: a failure here must not hide the report of the
 * reviews that did move — a retry would then read "already assigned to that reviewer" and the user
 * would never learn what happened.
 */
export async function assignFutureReviewer(
  result: BulkReviewResult,
  rows: readonly ClientReview[],
  reviewer: SystemUser,
  staffProfiles: readonly StaffProfile[],
): Promise<string> {
  return (await futureReviewerOutcome(result, rows, reviewer, staffProfiles)).text;
}

/**
 * The same, with whether it worked — so the report's tone comes from the outcome, never from
 * matching English words in a sentence that may now be Spanish.
 */
export async function futureReviewerOutcome(
  result: BulkReviewResult,
  rows: readonly ClientReview[],
  reviewer: SystemUser,
  staffProfiles: readonly StaffProfile[],
): Promise<{ text: string; ok: boolean }> {
  const staffId = staffProfiles.find((p) => p.systemUserId === reviewer.systemuserid)?.staffId;
  if (!staffId) return { text: i18n.t('bulk.future.noProfile', { name: reviewer.fullname }), ok: false };
  const doneIds = new Set(result.results.filter((r) => r.outcome === 'done').map((r) => r.id));
  const companyIds = [...new Set(rows.filter((r) => doneIds.has(r.id)).map((r) => r.clientId).filter(Boolean))];
  if (companyIds.length === 0) return { text: '', ok: true };
  try {
    const a = await staffProfilesApi.bulkSetReviewerAssignments({ companyIds, reviewerStaffId: staffId });
    const already = a.unchanged ? i18n.t('bulk.future.already', { count: a.unchanged }) : '';
    return {
      text: i18n.t('bulk.future.assigned', { clients: clientsCount(a.assigned), name: reviewer.fullname, already }),
      ok: true,
    };
  } catch (e) {
    return { text: i18n.t('bulk.future.failed', { error: apiErrorText(e) }), ok: false };
  }
}

// ── Action bar + dialog ──────────────────────────────────────────────────────────────────────

export function BulkActionBar({
  selection,
  rows,
  reviewers,
  staffProfiles,
}: {
  selection: ReviewSelection;
  /** The selected review objects (for counts and names). */
  rows: readonly ClientReview[];
  reviewers: readonly SystemUser[];
  staffProfiles: readonly StaffProfile[];
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [action, setAction] = useState<BulkReviewAction | null>(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [reviewerId, setReviewerId] = useState('');
  const [alsoFuture, setAlsoFuture] = useState(true);
  const [preview, setPreview] = useState<BulkReviewResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [warnTone, setWarnTone] = useState(false);

  const ids = rows.map((r) => r.id);
  // The client-assignment writer is admin-gated; for everyone else the staff-profile query 403s and
  // comes back empty, so the option is not offered rather than offered and then refused.
  const canSetFuture = staffProfiles.length > 0;
  const reviewer = reviewers.find((u) => u.systemuserid === reviewerId);
  const picked = CANCEL_REASONS.find((r) => r.value === reason);
  const paramsReady =
    action === 'cancel' ? !!reason && !(picked?.needsNote && !note.trim())
    : action === 'reassign-reviewer' ? !!reviewer
    : true;

  const request = (dryRun: boolean): BulkReviewRequest => ({
    action: action!,
    ids,
    dryRun,
    ...(action === 'cancel' ? { closeReason: reason, note: note.trim() || undefined } : {}),
    ...(action === 'reassign-reviewer' && reviewer ? { supervisorId: reviewer.systemuserid, supervisorName: reviewer.fullname } : {}),
  });

  // The dry run runs whenever the request is complete, so the dialog always shows the server's numbers.
  useEffect(() => {
    if (!action || !paramsReady || ids.length === 0) {
      setPreview(null);
      return;
    }
    let live = true;
    setError(null);
    runBulk(request(true))
      .then((p) => live && setPreview(p))
      .catch((e) => live && setError(apiErrorText(e)));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- request() closes over exactly these
  }, [action, reason, note, reviewerId, ids.join('|'), paramsReady]);

  const openFor = (a: BulkReviewAction) => {
    setAction(a);
    setReason('');
    setNote('');
    setReviewerId('');
    setPreview(null);
    setError(null);
  };
  const close = () => setAction(null);

  const execute = async () => {
    if (!action) return;
    setBusy(true);
    setError(null);
    try {
      const result = await runBulk(request(false));
      const extra =
        action === 'reassign-reviewer' && canSetFuture && alsoFuture && reviewer
          ? await futureReviewerOutcome(result, rows, reviewer, staffProfiles)
          : { text: '', ok: true };
      setReport(describeOutcome(action, result) + extra.text);
      setWarnTone(reportWarns(result, extra.ok));
      selection.clear();
      setAction(null);
      await queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
      await queryClient.invalidateQueries({ queryKey: REVIEWER_ASSIGNMENTS_QUERY_KEY });
      if (action === 'complete') await queryClient.invalidateQueries({ queryKey: MY_MOMENTS_QUERY_KEY });
    } catch (e) {
      setError(apiErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const doneCount = preview?.summary.done ?? 0;

  return (
    <>
      {report ? (
        <MessageBar intent={warnTone ? 'warning' : 'success'} style={{ marginBottom: '8px' }}>
          <MessageBarBody>
            {report}{' '}
            <Button size="small" appearance="transparent" onClick={() => setReport(null)}>
              {t('bulk.dismiss')}
            </Button>
          </MessageBarBody>
        </MessageBar>
      ) : null}
      {selection.notice ? (
        <MessageBar intent="info" style={{ marginBottom: '8px' }}>
          <MessageBarBody>
            {selection.notice}{' '}
            <Button size="small" appearance="transparent" onClick={selection.dismissNotice}>
              {t('bulk.dismiss')}
            </Button>
          </MessageBarBody>
        </MessageBar>
      ) : null}
      {rows.length > 0 ? (
        <div
          role="toolbar"
          aria-label={t('bulk.toolbar')}
          style={{
            display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', marginBottom: '8px',
            borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground2,
          }}
        >
          <Text weight="semibold">{t('bulk.selected', { reviews: reviewsCount(rows.length) })}</Text>
          <Button size="small" onClick={() => openFor('start')}>{t('bulk.buttons.start')}</Button>
          <Button size="small" onClick={() => openFor('complete')}>{t('bulk.buttons.complete')}</Button>
          <Button size="small" onClick={() => openFor('cancel')}>{t('bulk.buttons.cancel')}</Button>
          <Button size="small" onClick={() => openFor('reassign-reviewer')}>{t('bulk.buttons.reassign')}</Button>
          <Button size="small" appearance="subtle" onClick={selection.clear}>{t('bulk.buttons.clear')}</Button>
        </div>
      ) : null}

      <Dialog open={action !== null} onOpenChange={(_, d) => !d.open && close()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{action ? t('bulk.dialogTitle', { label: VERB.label(action), selected: t('bulk.count.selectedReview', { count: rows.length }) }) : ''}</DialogTitle>
            <DialogContent>
              {action === 'cancel' ? (
                <>
                  <Field label={t('bulk.reason')} required>
                    <Dropdown
                      placeholder={t('bulk.chooseReason')}
                      value={picked ? cancelReasonLabel(picked.value) ?? picked.label : ''}
                      selectedOptions={reason ? [reason] : []}
                      onOptionSelect={(_, d) => setReason(d.optionValue ?? '')}
                    >
                      {CANCEL_REASONS.map((r) => (
                        <Option key={r.value} value={r.value}>{cancelReasonLabel(r.value) ?? r.label}</Option>
                      ))}
                    </Dropdown>
                  </Field>
                  <Field label={picked?.needsNote ? t('bulk.noteRequired') : t('bulk.note')} style={{ marginTop: '8px' }}>
                    <Textarea value={note} onChange={(_, d) => setNote(d.value)} />
                  </Field>
                </>
              ) : null}
              {action === 'reassign-reviewer' ? (
                <>
                  <Field label={t('bulk.newReviewer')} required>
                    <Dropdown
                      placeholder={t('bulk.chooseReviewer')}
                      value={reviewer?.fullname ?? ''}
                      selectedOptions={reviewerId ? [reviewerId] : []}
                      onOptionSelect={(_, d) => setReviewerId(d.optionValue ?? '')}
                    >
                      {reviewers.map((u) => (
                        <Option key={u.systemuserid} value={u.systemuserid}>{u.fullname}</Option>
                      ))}
                    </Dropdown>
                  </Field>
                  {canSetFuture ? (
                  <Checkbox
                    style={{ marginTop: '8px' }}
                    checked={alsoFuture}
                    onChange={(_, d) => setAlsoFuture(d.checked === true)}
                    label={t('bulk.alsoFuture')}
                  />
                  ) : null}
                </>
              ) : null}

              <div style={{ marginTop: '12px' }} aria-live="polite">
                {!paramsReady ? null : preview && action ? (
                  describeDryRun(action, preview, rows, action === 'reassign-reviewer' ? reviewer?.fullname : undefined).map((line, i) => (
                    <Text block key={i} weight={i === 0 ? 'semibold' : 'regular'}>{line}</Text>
                  ))
                ) : (
                  <Text>{t('bulk.checking')}</Text>
                )}
              </div>
              {error ? (
                <Text block style={{ color: tokens.colorPaletteRedForeground1, marginTop: '8px' }}>{error}</Text>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={close}>{t('common.back')}</Button>
              <Button appearance="primary" disabled={!preview || doneCount === 0 || busy} onClick={execute}>
                {action ? t('bulk.confirm', { verb: VERB.verb(action), reviews: reviewsCount(doneCount) }) : ''}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
